import { ModalName } from 'app/enums';
import { getUserInfo } from 'app/store/session/repository';
import React, { FC, ReactElement, useCallback, useMemo, useState } from 'react';
import { Modal } from 'shared/components/Modal';
import { withModalOpenChecker } from 'shared/HOC/withModalOpenChecker';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { modalsSelectorFactory } from 'app/store/modal/selectors';
import { PlatformLinkingConnectionStatus } from '@okletsplay/core/src/modules/platform-linking/enums';
import { useStyles } from './LinkingProgressModal.styles';
import { LinkingFailed } from 'modules/platform-linking/components/LinkingProgressModal/LinkingFailed';
import { LinkingInProgress } from 'modules/platform-linking/components/LinkingProgressModal/LinkingInProgress';
import { LinkedSuccessfully } from 'modules/platform-linking/components/LinkingProgressModal/LinkedSuccessfully';
import { PlatformLinkingStatus } from 'modules/platform-linking/enums';
import { useSocketSubscriber } from 'app/hooks/socket';
import { SocketClientMessage } from '@okletsplay/core';
import { LinkingProgressModalActions } from 'modules/platform-linking/components/LinkingProgressModal/LinkingProgressModalActions';
import { PuuidMissing } from './PuuidMissing';

const PlatformLinkingModalComponent: FC = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const provider = useSelector(modalsSelectorFactory.useModalPayload(ModalName.PlatformLinking));

  const [platformLinkingStatus, setPlatformLinkingStatus] = useState<PlatformLinkingStatus>(
    provider?.connectionStatus === PlatformLinkingConnectionStatus.Connected
      ? PlatformLinkingStatus.Success
      : PlatformLinkingStatus.InProgress
  );

  const updateStatus = useCallback(
    (status: PlatformLinkingStatus) => {
      setPlatformLinkingStatus(status);
      dispatch(getUserInfo());
    },
    [dispatch, setPlatformLinkingStatus]
  );

  useSocketSubscriber([
    {
      action: SocketClientMessage.LinkStatusUpdatedSuccessfully,
      handler: () => updateStatus(PlatformLinkingStatus.Success),
    },
    {
      action: SocketClientMessage.LinkStatusUpdateFailed,
      handler: () => updateStatus(PlatformLinkingStatus.Failed),
    },
    {
      action: SocketClientMessage.LinkStatusPuuidMissing,
      handler: () => updateStatus(PlatformLinkingStatus.PuuidMissing),
    },
  ]);

  const modalBody: Record<PlatformLinkingStatus, ReactElement> = useMemo(() => {
    return {
      [PlatformLinkingStatus.InProgress]: (
        <LinkingInProgress
          platformName={provider?.platformName}
          authorizeRedirectURI={provider?.authorizeRedirectURI}
        />
      ),
      [PlatformLinkingStatus.Success]: <LinkedSuccessfully platformName={provider?.platformName} />,
      [PlatformLinkingStatus.Failed]: <LinkingFailed />,
      [PlatformLinkingStatus.PuuidMissing]: <PuuidMissing />,
    };
  }, [provider?.authorizeRedirectURI, provider?.platformName]);

  return (
    <Modal
      maxWidth="md"
      header={<Box />}
      isHeaderHighlighted={false}
      isFooterHighlighted={false}
      name={ModalName.PlatformLinking}
      disableEnforceFocus={true}
      actions={<LinkingProgressModalActions status={platformLinkingStatus} />}
    >
      <Box className={classes.root}>{modalBody[platformLinkingStatus]}</Box>
    </Modal>
  );
};

export const PlatformLinkingModal = withModalOpenChecker(
  PlatformLinkingModalComponent,
  ModalName.PlatformLinking
);
